import {
	Avatar,
	Badge,
	Box,
	Button,
	Card,
	Flex,
	Group,
	Menu,
	Modal,
	Stack,
	Switch,
	Tabs,
	Table,
	Text,
	TextInput,
	Textarea,
} from '@mantine/core'
import {
	IconDotsVertical,
	IconEdit,
	IconEye,
	IconEyeOff,
	IconPlus,
	IconTrash,
} from '@tabler/icons-react'
import dayjs from 'dayjs'
import { MRT_ColumnDef, MRT_PaginationState } from 'mantine-react-table'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
	approveContentModerationCase,
	createBadWord,
	createBadWordCategory,
	deleteBadWord,
	deleteBadWordCategory,
	getBadWordCategories,
	getBadWords,
	getContentModerationCases,
	hideContentModerationCase,
	updateBadWord,
	updateBadWordCategory,
} from 'src/apis/badwordAPI'
import CustomClearInput from 'src/components/CustomComponents/CustomClearInput'
import CustomSelect from 'src/components/CustomComponents/CustomSelect'
import CustomTooltip from 'src/components/CustomComponents/CustomTooltip'
import TableComponent from 'src/components/TableComponent'
import actionClasses from 'src/components/TableComponent/TableComponent.module.scss'
import { useLoading } from 'src/context/LoadingContext'
import { useModal } from 'src/context/ModalContext'
import { useQueryParamTab } from 'src/utils/tabQueryParam'
import { color, formatDateTime, size } from 'src/variable/constant'

const MATCH_TYPE_OPTIONS = [
	{ label: 'Contains', value: 'CONTAINS' },
	{ label: 'Exact', value: 'EXACT' },
]

const MATCH_TYPE_GUIDES = [
	{
		type: 'CONTAINS',
		meaning: 'Block khi content có chứa chuỗi gần giống hoặc substring.',
		keyword: 'bumble.com',
		block: 'bumble.com.vn, abumble.com, bumble.com123',
		notBlock: '-',
		usage: 'Dùng cho link/domain biến thể hoặc chuỗi cần bắt rộng.',
	},
	{
		type: 'EXACT',
		meaning:
			'Block khi content có chứa đúng từ/cụm độc lập, không dính liền vào từ/domain dài hơn.',
		keyword: 'bumble.com',
		block: 'vào bumble.com nha, bumble.com',
		notBlock: 'bumble.com.vn, abumble.com, bumble.com123',
		usage: 'Dùng cho từ/cụm cần chính xác.',
	},
]

const STATUS_OPTIONS = [
	{ label: 'All', value: 'ALL' },
	{ label: 'Active', value: 'true' },
	{ label: 'Inactive', value: 'false' },
]

const CASE_STATUS_OPTIONS = [
	{ label: 'All', value: 'ALL' },
	{ label: 'Pending', value: 'OPEN' },
	{ label: 'Approve', value: 'APPROVED' },
	{ label: 'Hidden', value: 'HIDDEN' },
]

const TABLE_TYPE_OPTIONS = [
	{ label: 'All', value: 'ALL' },
	{ label: 'Post', value: 'POST' },
	{ label: 'Comment', value: 'COMMENT' },
]

const CATEGORY_FIELDS_PARAM = JSON.stringify(['$all'])
const BAD_WORD_FIELDS_PARAM = JSON.stringify(['$all'])
const CONTENT_MODERATION_CASE_FIELDS_PARAM = JSON.stringify(['$all'])

const caseStatusMeta = {
	OPEN: { label: 'Pending', color: 'yellow' },
	APPROVED: { label: 'Approve', color: 'green' },
	HIDDEN: { label: 'Hidden', color: 'red' },
}

const getRows = (res: any) => res?.data?.results?.objects?.rows || []
const getCount = (res: any) => res?.data?.results?.objects?.count || 0

const TextCell = ({ value, maxWidth = size.medium }) => (
	<CustomTooltip label={value || '-'} maxWidth={maxWidth} />
)

const PreviewCell = ({
	title,
	description,
	maxWidth = 300,
}: {
	title?: string
	description?: string
	maxWidth?: number
}) => (
	<CustomTooltip
		label={
			<Stack spacing={6} sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
				<Text size="sm" weight={700}>
					{title || '-'}
				</Text>
				{description && <Text size="xs">{description}</Text>}
			</Stack>
		}
		multiline
		width={520}
		sx={{ maxWidth: 520, whiteSpace: 'normal', wordBreak: 'break-word' }}
	>
		<Stack spacing={2} sx={{ maxWidth }}>
			<Text weight={700} size="sm" lineClamp={1}>
				{title || '-'}
			</Text>
			{description && (
				<Text size="xs" color="dimmed" lineClamp={2}>
					{description}
				</Text>
			)}
		</Stack>
	</CustomTooltip>
)

const UserPill = ({
	user,
	badge,
	onClick,
}: {
	user?: any
	badge: string
	onClick?: () => void
}) => {
	if (!user?.id && !user?.name && !user?.email && !user?.phone) {
		return <Text size="sm">-</Text>
	}

	const subtitle = user?.phone || user?.email || user?.id || '-'
	return (
		<Flex
			align="center"
			gap={6}
			onClick={onClick}
			sx={{
				maxWidth: 220,
				padding: '3px 8px 3px 4px',
				borderRadius: 999,
				cursor: onClick ? 'pointer' : 'default',
				backgroundColor: '#f8fafc',
				border: '1px solid #e5e7eb',
				'&:hover': onClick
					? {
							backgroundColor: '#eef2ff',
						}
					: undefined,
			}}
		>
			<Avatar src={user?.avatar} radius={50} size={24} />
			<Box sx={{ minWidth: 0, flex: 1 }}>
				<Text size="xs" weight={700} lineClamp={1}>
					{user?.name || subtitle}
				</Text>
				<Text size="xs" color="dimmed" lineClamp={1}>
					{subtitle}
				</Text>
			</Box>
			<Badge size="xs" radius={6} variant="light" color="blue">
				{badge}
			</Badge>
		</Flex>
	)
}

const buildWhereParams = (where: Record<string, any>) => {
	const entries = Object.entries(where).filter(([, value]) => {
		if (value === undefined || value === null || value === '') return false
		return value !== 'ALL'
	})

	return entries.length
		? {
				where: JSON.stringify(Object.fromEntries(entries)),
			}
		: {}
}

const getCategoryOptions = (categories: any[], includeAll = false) => [
	...(includeAll ? [{ label: 'All', value: 'ALL' }] : []),
	...categories.map((item) => ({
		label: item.name || item.code || item.id,
		value: item.id,
	})),
]

function CategoryModal({
	opened,
	item,
	onClose,
	onSubmit,
}: {
	opened: boolean
	item: any
	onClose: () => void
	onSubmit: (payload: any) => Promise<void>
}) {
	const [form, setForm] = useState({
		name: '',
		description: '',
		status: true,
	})

	useEffect(() => {
		setForm({
			name: item?.name || '',
			description: item?.description || '',
			status: item?.status !== false,
		})
	}, [item, opened])

	const handleSubmit = async () => {
		await onSubmit({
			name: form.name.trim(),
			description: form.description.trim() || null,
			status: form.status,
		})
	}

	return (
		<Modal
			opened={opened}
			onClose={onClose}
			title={item?.id ? 'Edit category' : 'Create category'}
			centered
		>
			<Stack>
				<TextInput
					label="Name"
					value={form.name}
					onChange={(event) => {
						const { value } = event.currentTarget
						setForm((prev) => ({ ...prev, name: value }))
					}}
				/>
				<Textarea
					label="Description"
					value={form.description}
					onChange={(event) => {
						const { value } = event.currentTarget
						setForm((prev) => ({
							...prev,
							description: value,
						}))
					}}
				/>
				<Switch
					label="Active"
					checked={form.status}
					onChange={(event) => {
						const { checked } = event.currentTarget
						setForm((prev) => ({
							...prev,
							status: checked,
						}))
					}}
				/>
				<Group position="right">
					<Button variant="default" onClick={onClose}>
						Cancel
					</Button>
					<Button
						color="green"
						onClick={handleSubmit}
						disabled={!form.name.trim()}
					>
						Save
					</Button>
				</Group>
			</Stack>
		</Modal>
	)
}

function BadWordModal({
	opened,
	item,
	categories,
	onClose,
	onSubmit,
}: {
	opened: boolean
	item: any
	categories: any[]
	onClose: () => void
	onSubmit: (payload: any) => Promise<void>
}) {
	const [form, setForm] = useState({
		category_id: '',
		word: '',
		match_type: 'CONTAINS',
		status: true,
	})

	useEffect(() => {
		setForm({
			category_id: item?.category_id || categories?.[0]?.id || '',
			word: item?.word || '',
			match_type: item?.match_type || 'CONTAINS',
			status: item?.status !== false,
		})
	}, [categories, item, opened])

	const handleSubmit = async () => {
		await onSubmit({
			category_id: form.category_id,
			word: form.word.trim(),
			match_type: form.match_type,
			status: form.status,
		})
	}

	return (
		<Modal
			opened={opened}
			onClose={onClose}
			title={item?.id ? 'Edit badword' : 'Create badword'}
			centered
		>
			<Stack>
				<CustomSelect
					label="Category"
					data={getCategoryOptions(categories)}
					value={form.category_id}
					onChange={(value) =>
						setForm((prev) => ({ ...prev, category_id: value || '' }))
					}
				/>
				<TextInput
					label="Word"
					value={form.word}
					onChange={(event) => {
						const { value } = event.currentTarget
						setForm((prev) => ({ ...prev, word: value }))
					}}
				/>
				<CustomSelect
					label="Match type"
					data={MATCH_TYPE_OPTIONS}
					value={form.match_type}
					onChange={(value) =>
						setForm((prev) => ({
							...prev,
							match_type: value || 'CONTAINS',
						}))
					}
				/>
				<Text size="sm" color="dimmed">
					Contains: block when content includes this sequence, even inside
					larger text.
				</Text>
				<Text size="sm" color="dimmed">
					Exact: block when content contains this exact word/phrase, not as
					part of a larger word/domain.
				</Text>
				<Switch
					label="Active"
					checked={form.status}
					onChange={(event) => {
						const { checked } = event.currentTarget
						setForm((prev) => ({
							...prev,
							status: checked,
						}))
					}}
				/>
				<Group position="right">
					<Button variant="default" onClick={onClose}>
						Cancel
					</Button>
					<Button
						color="green"
						onClick={handleSubmit}
						disabled={!form.category_id || !form.word.trim()}
					>
						Save
					</Button>
				</Group>
			</Stack>
		</Modal>
	)
}

function ReviewModal({
	opened,
	item,
	action,
	onClose,
	onSubmit,
}: {
	opened: boolean
	item: any
	action: 'approve' | 'hide'
	onClose: () => void
	onSubmit: (note: string) => Promise<void>
}) {
	const [note, setNote] = useState('')

	useEffect(() => {
		setNote('')
	}, [opened, item])

	return (
		<Modal
			opened={opened}
			onClose={onClose}
			title={action === 'approve' ? 'Approve content' : 'Hidden content'}
			centered
		>
			<Stack>
				<Text color="dimmed" size="sm">
					{item?.snapshot_text || '-'}
				</Text>
				<Textarea
					label="Reviewer note"
					value={note}
					onChange={(event) => {
						const { value } = event.currentTarget
						setNote(value)
					}}
					minRows={4}
				/>
				<Group position="right">
					<Button variant="default" onClick={onClose}>
						Cancel
					</Button>
					<Button
						color={action === 'approve' ? 'green' : 'red'}
						onClick={() => onSubmit(note)}
					>
						{action === 'approve' ? 'Approve' : 'Hidden'}
					</Button>
				</Group>
			</Stack>
		</Modal>
	)
}

function MatchTypeGuideTable() {
	return (
		<Card withBorder radius="md" p="md" sx={{ overflow: 'auto' }}>
			<Stack spacing={10}>
				<Text fw={700}>Hướng dẫn cấu hình Match type</Text>
				<Table striped highlightOnHover withBorder withColumnBorders>
					<thead>
						<tr>
							<th>Match type</th>
							<th>Ý nghĩa</th>
							<th>Keyword ví dụ</th>
							<th>Sẽ block</th>
							<th>Không block</th>
							<th>Khi nào dùng</th>
						</tr>
					</thead>
					<tbody>
						{MATCH_TYPE_GUIDES.map((item) => (
							<tr key={item.type}>
								<td>
									<Badge color={item.type === 'EXACT' ? 'blue' : 'green'}>
										{item.type}
									</Badge>
								</td>
								<td>{item.meaning}</td>
								<td>{item.keyword}</td>
								<td>{item.block}</td>
								<td>{item.notBlock}</td>
								<td>{item.usage}</td>
							</tr>
						))}
					</tbody>
				</Table>
			</Stack>
		</Card>
	)
}

function KeywordsTab() {
	const { openError, openModal } = useModal()
	const { openLoadingContext, closeLoadingContext } = useLoading()
	const didMountCategoryFilter = useRef(false)
	const didMountWordFilter = useRef(false)

	const [categories, setCategories] = useState<any[]>([])
	const [allCategories, setAllCategories] = useState<any[]>([])
	const [categoryTotal, setCategoryTotal] = useState(0)
	const [categoryLoading, setCategoryLoading] = useState(false)
	const [categoryPagination, setCategoryPagination] =
		useState<MRT_PaginationState>({
			pageIndex: 1,
			pageSize: 10,
		})
	const [categoryFilter, setCategoryFilter] = useState({
		search: '',
		status: 'ALL',
	})
	const [categoryModalItem, setCategoryModalItem] = useState<any | null>(null)
	const [categoryModalOpen, setCategoryModalOpen] = useState(false)

	const [badWords, setBadWords] = useState<any[]>([])
	const [badWordTotal, setBadWordTotal] = useState(0)
	const [badWordLoading, setBadWordLoading] = useState(false)
	const [badWordPagination, setBadWordPagination] =
		useState<MRT_PaginationState>({
			pageIndex: 1,
			pageSize: 10,
		})
	const [badWordFilter, setBadWordFilter] = useState({
		search: '',
		category_id: 'ALL',
		status: 'ALL',
	})
	const [badWordModalItem, setBadWordModalItem] = useState<any | null>(null)
	const [badWordModalOpen, setBadWordModalOpen] = useState(false)

	const categoryOptions = useMemo(
		() => getCategoryOptions(allCategories, true),
		[allCategories],
	)
	const categoryById = useMemo(() => {
		return allCategories.reduce((map, item) => {
			map[item.id] = item
			return map
		}, {})
	}, [allCategories])

	const fetchCategories = useCallback(async () => {
		setCategoryLoading(true)
		try {
			const where: any = {}
			if (categoryFilter.status !== 'ALL') {
				where.status = categoryFilter.status === 'true'
			}
			if (categoryFilter.search.trim()) {
				where.name = { $iLike: `%${categoryFilter.search.trim()}%` }
			}
			const res = await getBadWordCategories({
				page: categoryPagination.pageIndex,
				limit: categoryPagination.pageSize,
				fields: CATEGORY_FIELDS_PARAM,
				...buildWhereParams(where),
			})
			const rows = getRows(res)
			setCategories(rows)
			setCategoryTotal(getCount(res))
		} catch (error) {
			openError(error)
		} finally {
			setCategoryLoading(false)
		}
	}, [categoryFilter, categoryPagination, openError])

	const fetchAllCategories = useCallback(async () => {
		try {
			const res = await getBadWordCategories({
				page: 1,
				limit: 10,
				fields: CATEGORY_FIELDS_PARAM,
			})
			setAllCategories(getRows(res))
		} catch (error) {
			openError(error)
		}
	}, [openError])

	const fetchBadWords = useCallback(async () => {
		setBadWordLoading(true)
		try {
			const where: any = {}
			if (badWordFilter.category_id !== 'ALL') {
				where.category_id = badWordFilter.category_id
			}
			if (badWordFilter.status !== 'ALL') {
				where.status = badWordFilter.status === 'true'
			}
			if (badWordFilter.search.trim()) {
				where.word = { $iLike: `%${badWordFilter.search.trim()}%` }
			}

			const res = await getBadWords({
				page: badWordPagination.pageIndex,
				limit: badWordPagination.pageSize,
				fields: BAD_WORD_FIELDS_PARAM,
				...buildWhereParams(where),
			})
			const rows = getRows(res)
			setBadWords(
				rows.map((item, index) => ({
					...item,
					no:
						index +
						1 +
						badWordPagination.pageSize *
							(badWordPagination.pageIndex - 1),
					category_name:
						categoryById[item.category_id]?.name || item.category_id || '-',
					created_display: item.created_at
						? dayjs(item.created_at).format(formatDateTime)
						: '-',
				})),
			)
			setBadWordTotal(getCount(res))
		} catch (error) {
			openError(error)
		} finally {
			setBadWordLoading(false)
		}
	}, [badWordFilter, badWordPagination, categoryById, openError])

	const handleSaveCategory = useCallback(
		async (payload: any) => {
			openLoadingContext()
			try {
				if (categoryModalItem?.id) {
					await updateBadWordCategory(categoryModalItem.id, payload)
				} else {
					await createBadWordCategory(payload)
				}
				setCategoryModalOpen(false)
				await fetchCategories()
				await fetchAllCategories()
				openModal({ type: 'success', label: 'Category saved successfully' })
			} catch (error) {
				openError(error)
			} finally {
				closeLoadingContext()
			}
		},
		[
			categoryModalItem,
			closeLoadingContext,
			fetchCategories,
			fetchAllCategories,
			openError,
			openLoadingContext,
			openModal,
		],
	)

	const handleSaveBadWord = useCallback(
		async (payload: any) => {
			openLoadingContext()
			try {
				if (badWordModalItem?.id) {
					await updateBadWord(badWordModalItem.id, payload)
				} else {
					await createBadWord(payload)
				}
				setBadWordModalOpen(false)
				await fetchBadWords()
				openModal({ type: 'success', label: 'Badword saved successfully' })
			} catch (error) {
				openError(error)
			} finally {
				closeLoadingContext()
			}
		},
		[
			badWordModalItem,
			closeLoadingContext,
			fetchBadWords,
			openError,
			openLoadingContext,
			openModal,
		],
	)

	const handleDeleteCategory = useCallback(
		(row: any) => {
			openModal({
				type: 'confirm',
				label: <div>Do you want to delete this category?</div>,
				handleConfirm: async () => {
					openLoadingContext()
					try {
						await deleteBadWordCategory(row.original.id)
						await fetchCategories()
						await fetchAllCategories()
						openModal({
							type: 'success',
							label: 'Category deleted successfully',
						})
					} catch (error) {
						openError(error)
					} finally {
						closeLoadingContext()
					}
				},
			})
		},
		[
			closeLoadingContext,
			fetchCategories,
			fetchAllCategories,
			openError,
			openLoadingContext,
			openModal,
		],
	)

	const handleDeleteBadWord = useCallback(
		(row: any) => {
			openModal({
				type: 'confirm',
				label: <div>Do you want to delete this badword?</div>,
				handleConfirm: async () => {
					openLoadingContext()
					try {
						await deleteBadWord(row.original.id)
						await fetchBadWords()
						openModal({
							type: 'success',
							label: 'Badword deleted successfully',
						})
					} catch (error) {
						openError(error)
					} finally {
						closeLoadingContext()
					}
				},
			})
		},
		[
			closeLoadingContext,
			fetchBadWords,
			openError,
			openLoadingContext,
			openModal,
		],
	)

	const categoryColumns = useMemo<MRT_ColumnDef[]>(
		() => [
			{ accessorKey: 'name', header: 'Name', size: size.text },
			{
				accessorKey: 'description',
				header: 'Description',
				size: size.medium,
				Cell: ({ cell }) => <TextCell value={cell.getValue<string>()} />,
			},
			{
				accessorKey: 'status',
				header: 'Status',
				size: size.zero,
				Cell: ({ cell }) => (
					<Badge color={cell.getValue<boolean>() ? 'green' : 'gray'}>
						{cell.getValue<boolean>() ? 'Active' : 'Inactive'}
					</Badge>
				),
			},
		],
		[],
	)

	const badWordColumns = useMemo<MRT_ColumnDef[]>(
		() => [
			{ accessorKey: 'no', header: 'No', size: size.no },
			{ accessorKey: 'word', header: 'Word', size: size.text },
			{ accessorKey: 'category_name', header: 'Category', size: size.text },
			{ accessorKey: 'match_type', header: 'Match type', size: size.text },
			{
				accessorKey: 'status',
				header: 'Status',
				size: size.zero,
				Cell: ({ cell }) => (
					<Badge color={cell.getValue<boolean>() ? 'green' : 'gray'}>
						{cell.getValue<boolean>() ? 'Active' : 'Inactive'}
					</Badge>
				),
			},
			{ accessorKey: 'created_display', header: 'Created at', size: size.text },
		],
		[],
	)

	const renderCategoryActions = useCallback(
		(row) => (
			<Flex gap={10} align="center">
				<CustomTooltip label="Edit">
					<IconEdit
						size={20}
						style={{ cursor: 'pointer', color: color.Blue }}
						onClick={() => {
							setCategoryModalItem(row.original)
							setCategoryModalOpen(true)
						}}
					/>
				</CustomTooltip>
				<CustomTooltip label="Delete">
					<IconTrash
						size={20}
						style={{ cursor: 'pointer', color: 'red' }}
						onClick={() => handleDeleteCategory(row)}
					/>
				</CustomTooltip>
			</Flex>
		),
		[handleDeleteCategory],
	)

	const renderBadWordActions = useCallback(
		(row) => (
			<Flex gap={10} align="center">
				<CustomTooltip label="Edit">
					<IconEdit
						size={20}
						style={{ cursor: 'pointer', color: color.Blue }}
						onClick={() => {
							setBadWordModalItem(row.original)
							setBadWordModalOpen(true)
						}}
					/>
				</CustomTooltip>
				<CustomTooltip label="Delete">
					<IconTrash
						size={20}
						style={{ cursor: 'pointer', color: 'red' }}
						onClick={() => handleDeleteBadWord(row)}
					/>
				</CustomTooltip>
			</Flex>
		),
		[handleDeleteBadWord],
	)

	useEffect(() => {
		fetchCategories()
	}, [fetchCategories])

	useEffect(() => {
		fetchAllCategories()
	}, [fetchAllCategories])

	useEffect(() => {
		fetchBadWords()
	}, [fetchBadWords])

	useEffect(() => {
		if (!didMountCategoryFilter.current) {
			didMountCategoryFilter.current = true
			return
		}
		const timer = setTimeout(() => {
			setCategoryPagination((prev) => ({ ...prev, pageIndex: 1 }))
		}, 400)
		return () => clearTimeout(timer)
	}, [categoryFilter])

	useEffect(() => {
		if (!didMountWordFilter.current) {
			didMountWordFilter.current = true
			return
		}
		const timer = setTimeout(() => {
			setBadWordPagination((prev) => ({ ...prev, pageIndex: 1 }))
		}, 400)
		return () => clearTimeout(timer)
	}, [badWordFilter])

	return (
		<Stack spacing={18}>
			<Card withBorder radius="md" p="md" sx={{ overflow: 'visible' }}>
				<Flex justify="space-between" gap={12} wrap="wrap" align="end" mb={12}>
					<Flex gap={8} wrap="wrap" align="end">
						<CustomClearInput
							value={categoryFilter.search}
							onChange={(value) =>
								setCategoryFilter((prev) => ({ ...prev, search: value }))
							}
							placeholder="Search category"
						/>
						<CustomSelect
							label="Status"
							data={STATUS_OPTIONS}
							value={categoryFilter.status}
							onChange={(value) =>
								setCategoryFilter((prev) => ({
									...prev,
									status: value || 'ALL',
								}))
							}
						/>
					</Flex>
					<Button
						color="green"
						leftIcon={<IconPlus size={16} />}
						onClick={() => {
							setCategoryModalItem(null)
							setCategoryModalOpen(true)
						}}
					>
						Add category
					</Button>
				</Flex>
				<TableComponent
					columns={categoryColumns}
					total={categoryTotal}
					data={categories}
					pagination={categoryPagination}
					loading={categoryLoading}
					handleChangePage={(page) =>
						setCategoryPagination((prev) => ({ ...prev, pageIndex: page }))
					}
					_renderCustomActions={renderCategoryActions}
					syncQueryUrl={false}
				/>
			</Card>

			<MatchTypeGuideTable />

			<Card withBorder radius="md" p="md" sx={{ overflow: 'visible' }}>
				<Flex justify="space-between" gap={12} wrap="wrap" align="end" mb={12}>
					<Flex gap={8} wrap="wrap" align="end">
						<CustomClearInput
							value={badWordFilter.search}
							onChange={(value) =>
								setBadWordFilter((prev) => ({ ...prev, search: value }))
							}
							placeholder="Search badword"
						/>
						<CustomSelect
							label="Category"
							data={categoryOptions}
							value={badWordFilter.category_id}
							onChange={(value) =>
								setBadWordFilter((prev) => ({
									...prev,
									category_id: value || 'ALL',
								}))
							}
						/>
						<CustomSelect
							label="Status"
							data={STATUS_OPTIONS}
							value={badWordFilter.status}
							onChange={(value) =>
								setBadWordFilter((prev) => ({
									...prev,
									status: value || 'ALL',
								}))
							}
						/>
					</Flex>
					<Button
						color="green"
						leftIcon={<IconPlus size={16} />}
						onClick={() => {
							setBadWordModalItem(null)
							setBadWordModalOpen(true)
						}}
						disabled={!allCategories.length}
					>
						Add badword
					</Button>
				</Flex>
				<TableComponent
					columns={badWordColumns}
					total={badWordTotal}
					data={badWords}
					pagination={badWordPagination}
					loading={badWordLoading}
					handleChangePage={(page) =>
						setBadWordPagination((prev) => ({ ...prev, pageIndex: page }))
					}
					_renderCustomActions={renderBadWordActions}
					syncQueryUrl={false}
				/>
			</Card>

			<CategoryModal
				opened={categoryModalOpen}
				item={categoryModalItem}
				onClose={() => setCategoryModalOpen(false)}
				onSubmit={handleSaveCategory}
			/>
			<BadWordModal
				opened={badWordModalOpen}
				item={badWordModalItem}
				categories={allCategories}
				onClose={() => setBadWordModalOpen(false)}
				onSubmit={handleSaveBadWord}
			/>
		</Stack>
	)
}

function MatchedContentTab() {
	const navigate = useNavigate()
	const { openError, openModal } = useModal()
	const { openLoadingContext, closeLoadingContext } = useLoading()
	const didMountFilter = useRef(false)
	const [categories, setCategories] = useState<any[]>([])
	const [badWords, setBadWords] = useState<any[]>([])
	const [data, setData] = useState<any[]>([])
	const [total, setTotal] = useState(0)
	const [loading, setLoading] = useState(false)
	const [reviewModal, setReviewModal] = useState<{
		item: any
		action: 'approve' | 'hide'
	} | null>(null)
	const [pagination, setPagination] = useState<MRT_PaginationState>({
		pageIndex: 1,
		pageSize: 10,
	})
	const [filter, setFilter] = useState({
		status: 'ALL',
		table_type: 'ALL',
		bad_word_category_id: 'ALL',
		bad_word_id: 'ALL',
		search_text: '',
	})

	const categoryOptions = useMemo(
		() => getCategoryOptions(categories, true),
		[categories],
	)
	const badWordOptions = useMemo(
		() => [
			{ label: 'All', value: 'ALL' },
			...badWords.map((item) => ({
				label: item.word || item.id,
				value: item.id,
			})),
		],
		[badWords],
	)

	const fetchFilters = useCallback(async () => {
		try {
			const [categoryRes, badWordRes] = await Promise.all([
				getBadWordCategories({
					page: 1,
					limit: 10,
					fields: CATEGORY_FIELDS_PARAM,
				}),
				getBadWords({
					page: 1,
					limit: 3000,
					fields: BAD_WORD_FIELDS_PARAM,
				}),
			])
			setCategories(getRows(categoryRes))
			setBadWords(getRows(badWordRes))
		} catch (error) {
			openError(error)
		}
	}, [openError])

	const fetchCases = useCallback(async () => {
		setLoading(true)
		try {
			const res = await getContentModerationCases({
				page: pagination.pageIndex,
				limit: pagination.pageSize,
				fields: CONTENT_MODERATION_CASE_FIELDS_PARAM,
				status: filter.status,
				table_type: filter.table_type,
				bad_word_category_id: filter.bad_word_category_id,
				bad_word_id: filter.bad_word_id,
				search_text: filter.search_text.trim() || undefined,
			})
			const rows = getRows(res)
			setData(
				rows.map((item, index) => {
					const target =
						item.table_type === 'POST'
							? item.target_post
							: item.target_comment
					const status = caseStatusMeta[item.status] || {
						label: item.status || '-',
						color: 'gray',
					}
					return {
						...item,
						no:
							index +
							1 +
							pagination.pageSize * (pagination.pageIndex - 1),
						status_label: status.label,
						status_color: status.color,
						bad_word_display: item.bad_word?.word || item.matched_word || '-',
						category_display: item.bad_word_category?.name || '-',
						target_title:
							item.table_type === 'POST'
								? target?.title || item.table_type
								: item.table_type,
						target_preview:
							item.table_type === 'POST'
								? [target?.title, target?.description].filter(Boolean).join(' ')
								: target?.content || '-',
						created_display: item.created_at
							? dayjs(item.created_at).format(formatDateTime)
							: '-',
						reviewed_display: item.reviewed_at
							? dayjs(item.reviewed_at).format(formatDateTime)
							: '-',
					}
				}),
			)
			setTotal(getCount(res))
		} catch (error) {
			openError(error)
		} finally {
			setLoading(false)
		}
	}, [filter, openError, pagination])

	const handleReview = useCallback(
		async (note: string) => {
			if (!reviewModal?.item?.id) return
			openLoadingContext()
			try {
				if (reviewModal.action === 'approve') {
					await approveContentModerationCase(reviewModal.item.id, { note })
				} else {
					await hideContentModerationCase(reviewModal.item.id, { note })
				}
				setReviewModal(null)
				await fetchCases()
				openModal({ type: 'success', label: 'Moderation case updated' })
			} catch (error) {
				openError(error)
			} finally {
				closeLoadingContext()
			}
		},
		[
			closeLoadingContext,
			fetchCases,
			openError,
			openLoadingContext,
			openModal,
			reviewModal,
		],
	)

	const columns = useMemo<MRT_ColumnDef[]>(
		() => [
			{ accessorKey: 'no', header: 'No', size: size.zero },
			{
				accessorKey: 'status_label',
				header: 'Status',
				size: 110,
				Cell: ({ row }) => (
					<Badge radius={6} variant="light" color={(row.original as any).status_color}>
						{(row.original as any).status_label}
					</Badge>
				),
			},
			{
				accessorKey: 'table_type',
				header: 'Type',
				size: 90,
				Cell: ({ cell }) => (
					<Badge radius={6} variant="light" color="gray">
						{cell.getValue<string>() || '-'}
					</Badge>
				),
			},
			{
				accessorKey: 'bad_word_display',
				header: 'Matched word',
				size: 150,
				Cell: ({ row }) => (
					<Stack spacing={4} sx={{ maxWidth: 150 }}>
						<Text size="sm" weight={700} lineClamp={1}>
							{(row.original as any).bad_word_display}
						</Text>
						<Badge radius={6} color="green" variant="light">
							{(row.original as any).category_display}
						</Badge>
					</Stack>
				),
			},
			{
				accessorKey: 'snapshot_text',
				header: 'Snapshot',
				size: 280,
				maxSize: 320,
				Cell: ({ cell }) => (
					<PreviewCell title="Matched snapshot" description={cell.getValue<string>()} />
				),
			},
			{
				accessorKey: 'target_preview',
				header: 'Target',
				size: 300,
				maxSize: 340,
				Cell: ({ row }) => (
					<PreviewCell
						title={(row.original as any).target_title}
						description={(row.original as any).target_preview}
					/>
				),
			},
			{
				accessorKey: 'table_id',
				header: 'Target ID',
				size: 170,
				Cell: ({ cell }) => <TextCell value={cell.getValue<string>()} />,
			},
			{
				accessorKey: 'author',
				header: 'Author',
				size: 230,
				maxSize: 250,
				Cell: ({ row }) => {
					const author = (row.original as any).author
					return (
						<UserPill
							user={author}
							badge="AUTHOR"
							onClick={
								author?.id ? () => navigate(`/users/${author.id}`) : undefined
							}
						/>
					)
				},
			},
			{
				accessorKey: 'reviewer',
				header: 'Reviewer',
				size: 230,
				maxSize: 250,
				Cell: ({ row }) => (
					<UserPill user={(row.original as any).reviewer} badge="REVIEWER" />
				),
			},
			{ accessorKey: 'created_display', header: 'Created at', size: size.text },
			{ accessorKey: 'reviewed_display', header: 'Reviewed at', size: size.text },
			{
				accessorKey: 'reviewer_note',
				header: 'Reviewer note',
				size: size.medium,
				Cell: ({ cell }) => <TextCell value={cell.getValue<string>()} />,
			},
		],
		[navigate],
	)

	const renderActions = useCallback(
		(row) => {
			const original = row.original as any
			const canApprove =
				original.status === 'OPEN' || original.status === 'HIDDEN'
			const canHidden =
				original.status === 'OPEN' || original.status === 'APPROVED'

			return (
				<Flex gap={10} align="center">
					<Menu shadow="md" width={150} withinPortal position="bottom-end">
						<Menu.Target>
							<button
								type="button"
								className={`${actionClasses.actionButton} ${actionClasses.defaultAction}`}
							>
								<IconDotsVertical size={16} />
							</button>
						</Menu.Target>
						<Menu.Dropdown>
							{canApprove && (
								<Menu.Item
									icon={<IconEye size={15} />}
									onClick={() =>
										setReviewModal({ item: original, action: 'approve' })
									}
								>
									Approve
								</Menu.Item>
							)}
							{canHidden && (
								<Menu.Item
									color="red"
									icon={<IconEyeOff size={15} />}
									onClick={() =>
										setReviewModal({ item: original, action: 'hide' })
									}
								>
									Hidden
								</Menu.Item>
							)}
						</Menu.Dropdown>
					</Menu>
				</Flex>
			)
		},
		[],
	)

	useEffect(() => {
		fetchFilters()
	}, [fetchFilters])

	useEffect(() => {
		fetchCases()
	}, [fetchCases])

	useEffect(() => {
		if (!didMountFilter.current) {
			didMountFilter.current = true
			return
		}
		const timer = setTimeout(() => {
			setPagination((prev) => ({ ...prev, pageIndex: 1 }))
		}, 400)
		return () => clearTimeout(timer)
	}, [filter])

	const sectionTitleSx = { color: '#111827', fontWeight: 700 }
	const controlSx = { minWidth: 220 }

	return (
		<Stack spacing={16}>
			<Card
				withBorder
				radius={8}
				p="md"
				sx={{
					overflow: 'visible',
					backgroundColor: '#fbfdfc',
					borderColor: '#e3ece5',
				}}
			>
				<Flex gap={16} align="flex-start" sx={{ flexWrap: 'wrap' }}>
					<Flex gap={8} sx={{ flex: 1, minWidth: 520, flexWrap: 'wrap' }}>
						<Stack spacing={6}>
							<Text size="sm" sx={sectionTitleSx}>
								Search
							</Text>
							<CustomClearInput
								value={filter.search_text}
								onChange={(value) =>
									setFilter((prev) => ({ ...prev, search_text: value }))
								}
								placeholder="Search snapshot"
								sx={controlSx}
							/>
						</Stack>
						<Stack spacing={6}>
							<Text size="sm" sx={sectionTitleSx}>
								Filter
							</Text>
							<Flex gap={8} sx={{ flexWrap: 'wrap' }}>
								<CustomSelect
									data={CASE_STATUS_OPTIONS}
									value={filter.status}
									onChange={(value) =>
										setFilter((prev) => ({ ...prev, status: value || 'ALL' }))
									}
									sx={controlSx}
								/>
								<CustomSelect
									data={TABLE_TYPE_OPTIONS}
									value={filter.table_type}
									onChange={(value) =>
										setFilter((prev) => ({
											...prev,
											table_type: value || 'ALL',
										}))
									}
									sx={controlSx}
								/>
							</Flex>
						</Stack>
						<Stack spacing={6}>
							<Text size="sm" sx={sectionTitleSx}>
								Badword
							</Text>
							<Flex gap={8} sx={{ flexWrap: 'wrap' }}>
								<CustomSelect
									data={categoryOptions}
									value={filter.bad_word_category_id}
									onChange={(value) =>
										setFilter((prev) => ({
											...prev,
											bad_word_category_id: value || 'ALL',
											bad_word_id: 'ALL',
										}))
									}
									sx={controlSx}
								/>
								<CustomSelect
									data={badWordOptions}
									value={filter.bad_word_id}
									onChange={(value) =>
										setFilter((prev) => ({
											...prev,
											bad_word_id: value || 'ALL',
										}))
									}
									sx={controlSx}
								/>
							</Flex>
						</Stack>
					</Flex>
					<Card
						withBorder
						radius={8}
						p="md"
						sx={{
							minWidth: 150,
							backgroundColor: '#f0fdf4',
							borderColor: '#bbf7d0',
						}}
					>
						<Text size="xs" weight={700} color="green" transform="uppercase">
							Total cases
						</Text>
						<Text size={28} weight={700} color={color.CmsPrimary}>
							{total}
						</Text>
					</Card>
				</Flex>
			</Card>
			<TableComponent
				columns={columns}
				total={total}
				data={data}
				pagination={pagination}
				loading={loading}
				handleChangePage={(page) =>
					setPagination((prev) => ({ ...prev, pageIndex: page }))
				}
				_renderCustomActions={renderActions}
				actionColumnSize={72}
				syncQueryUrl={false}
			/>
			<ReviewModal
				opened={!!reviewModal}
				item={reviewModal?.item}
				action={reviewModal?.action || 'approve'}
				onClose={() => setReviewModal(null)}
				onSubmit={handleReview}
			/>
		</Stack>
	)
}

function BadwordPage() {
	const tabValues = ['matched-content', 'keywords'] as const
	const { activeTab, handleTabChange } = useQueryParamTab(
		tabValues,
		'matched-content',
	)

	return (
		<Stack sx={{ width: '100%' }} spacing={16}>
			<Text fw={800} size={28} color="#0f172a">
				Badword
			</Text>
			<Tabs value={activeTab} onTabChange={handleTabChange} keepMounted={false}>
				<Tabs.List>
					<Tabs.Tab value="matched-content" fw={700}>
						Matched Content
					</Tabs.Tab>
					<Tabs.Tab value="keywords" fw={700}>
						Keywords
					</Tabs.Tab>
				</Tabs.List>
				<Tabs.Panel value="matched-content" pt="md">
					<MatchedContentTab />
				</Tabs.Panel>
				<Tabs.Panel value="keywords" pt="md">
					<KeywordsTab />
				</Tabs.Panel>
			</Tabs>
		</Stack>
	)
}

export default React.memo(BadwordPage)
